import { motion } from "framer-motion";
import PageIntro from "./PageIntro";

const features = [
  {
    icon: "🧭",
    title: "Datengestützte Standortanalyse",
    text: "Auswertung von Bodenarten, Geologie, Wärmeleitfähigkeit und Grundwasser auf Basis öffentlich verfügbarer Landes- und Geodaten – ergänzt um eine orientierende Einordnung nach VDI 4640.",
  },
  {
    icon: "📐",
    title: "Gebäudemodell & Auslegung",
    text: "Heizlast-Abschätzung, Entzugsleistung qₐ und orientierende Dimensionierung von Erdkollektoren oder Erdsonden – inklusive möglicher Alternativen bei begrenzter Fläche.",
  },
  {
    icon: "💼",
    title: "Wirtschaftlichkeit & Vergleich",
    text: "Einordnung von Investitionskosten, Betriebskosten, 20-Jahres-Betrachtung und Förderoptionen – übersichtlich als Entscheidungs- und Vergleichsgrundlage.",
  },
];

const steps = [
  "Adresseingabe und Standortlokalisierung",
  "Analyse von Boden, Geologie, Wärmeleitfähigkeit und Grundwasser",
  "Gebäudemodell und Heizlastabschätzung",
  "Orientierende Dimensionierung der Erdwärmesysteme",
  "Kostenvergleich, Wirtschaftlichkeit und Einordnung",
];

export default function QuickReportHero() {
  return (
    <div className="mb-10">
      <PageIntro
        eyebrow="Standortanalyse & GeoReport"
        title={
          <>
            Erdwärme auf Ihrem Grundstück{" "}
            <span className="text-red-700">fundiert prüfen</span>
          </>
        }
        description={
          <>
            Der GeoReport kombiniert öffentlich verfügbare Geodaten,
            standortbezogene Modellannahmen und strukturierte
            Wärmebedarfsabschätzungen. So erhalten Sie eine fundierte erste
            Einschätzung, ob Erdkollektoren oder Erdsonden an Ihrem Standort
            grundsätzlich sinnvoll sein können.
          </>
        }
        badges={[
          "Untergrund & Grundwasser",
          "Orientierende Auslegung",
          "Kosten & Förderung",
        ]}
        className="mb-12"
      />

      <section className="mx-4 max-w-6xl rounded-3xl border border-slate-200 bg-white p-6 shadow-xl shadow-slate-200/40 sm:mx-6 sm:p-8 lg:p-10 xl:mx-auto">
        <div className="grid gap-5 md:grid-cols-3">
          {features.map((feature, index) => (
            <motion.article
              key={feature.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45 + index * 0.1 }}
              className="h-full rounded-2xl border border-slate-200 bg-slate-50 p-6"
            >
              <div className="mb-4 text-3xl" aria-hidden="true">
                {feature.icon}
              </div>
              <h2 className="text-lg font-semibold text-slate-950">
                {feature.title}
              </h2>
              <p className="mt-3 text-sm leading-6 text-slate-600 sm:text-base">
                {feature.text}
              </p>
            </motion.article>
          ))}
        </div>
      </section>

      <section className="mx-4 mt-10 max-w-6xl rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:mx-6 sm:p-8 lg:p-10 xl:mx-auto">
        <p className="mb-2 text-sm font-semibold text-red-700">
          Vom Standort zum Ergebnis
        </p>
        <h2 className="text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">
          So entsteht Ihr persönlicher GeoReport
        </h2>

        <div className="mt-7 grid gap-4 md:grid-cols-2">
          {steps.map((step, index) => (
            <motion.div
              key={step}
              initial={{ opacity: 0, x: -16 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.35 + index * 0.08 }}
              className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-5"
            >
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-700 text-sm font-bold text-white">
                {index + 1}
              </div>
              <p className="leading-7 text-slate-700">{step}</p>
            </motion.div>
          ))}
        </div>

        <p className="mt-7 max-w-3xl rounded-2xl border border-red-100 bg-red-50 p-5 font-medium leading-7 text-slate-800">
          Geben Sie Ihre Adresse und einige Gebäudedaten ein. Der GeoReport
          erstellt daraus automatisch eine erste standortbezogene Einschätzung.
        </p>
      </section>
    </div>
  );
}
